import { useEffect, useMemo, useRef, useState } from 'react'
import { IconCurrentLocation, IconMinus, IconPlus } from '@tabler/icons-react'
import { useI18n } from '../../../lib/i18n'

type WorldMap = { viewBox: string; locations: { name: string; id: string; path: string }[] }
type Hover = { name: string; code: string; views: number; x: number; y: number }
type MapView = { scale: number; x: number; y: number }
type Point = { x: number; y: number }

const DEFAULT_SCALE = 2.15
const MIN_SCALE = 1
const MAX_SCALE = 4

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value))
}

function clampView(view: MapView, width: number, height: number): MapView {
  const maxX = Math.max(24, (width * view.scale) / 2 - 24)
  const maxY = Math.max(24, (height * view.scale) / 2 - 24)
  return { ...view, x: clamp(view.x, -maxX, maxX), y: clamp(view.y, -maxY, maxY) }
}

function gestureFor(points: Point[]) {
  const first = points[0] ?? { x: 0, y: 0 }
  const second = points[1]
  return {
    center: second ? { x: (first.x + second.x) / 2, y: (first.y + second.y) / 2 } : first,
  }
}

/**
 * Choropleth world map of the audience: countries shade from gray (no traffic)
 * to green by view intensity. The ~200KB map paths are loaded
 * on demand so they never sit in the main bundle.
 */
export function AudienceMap({
  countries,
  labelFor,
}: {
  countries: { country: string; views: number }[]
  labelFor: (code: string) => string
}) {
  const { t } = useI18n()
  const [world, setWorld] = useState<WorldMap | null>(null)
  const [hover, setHover] = useState<Hover | null>(null)
  const [view, setView] = useState<MapView>({ scale: DEFAULT_SCALE, x: 0, y: 0 })
  const [dragging, setDragging] = useState(false)
  const viewportRef = useRef<HTMLDivElement>(null)
  const pathRefs = useRef(new Map<string, SVGPathElement>())
  const pointersRef = useRef(new Map<number, Point>())
  const gestureRef = useRef<ReturnType<typeof gestureFor> | null>(null)
  const centeredCountryRef = useRef<string | null>(null)

  useEffect(() => {
    let alive = true
    void import('@svg-maps/world').then((mod) => { if (alive) setWorld(mod.default) })
    return () => { alive = false }
  }, [])

  const byCode = useMemo(() => {
    const map = new Map<string, number>()
    for (const c of countries) {
      // 'XX' is the placeholder the worker stores when Cloudflare has no country.
      if (c.country && c.country !== 'XX') map.set(c.country.toUpperCase(), c.views)
    }
    return map
  }, [countries])

  const maxViews = useMemo(
    () => Math.max(1, ...[...byCode.values()]),
    [byCode],
  )

  const dominantCountry = useMemo(() => countries.reduce<{ country: string; views: number } | null>((best, country) => (
    country.country && country.country !== 'XX' && (!best || country.views > best.views) ? country : best
  ), null)?.country.toUpperCase() ?? null, [countries])

  // sqrt keeps small countries visible instead of washing out next to a giant.
  function fillFor(code: string): string {
    const views = byCode.get(code) ?? 0
    if (views <= 0) return 'var(--dash-map-empty)'
    const t = Math.min(1, Math.sqrt(views / maxViews))
    const pct = Math.round(18 + t * 82)
    return `color-mix(in srgb, var(--dash-map-active) ${pct}%, var(--dash-map-empty))`
  }

  function handleMove(event: React.MouseEvent<SVGPathElement>, loc: { name: string; id: string }) {
    if (dragging) return
    const bounds = viewportRef.current?.getBoundingClientRect()
    if (!bounds) return
    const code = loc.id.toUpperCase()
    setHover({
      name: labelFor(code) || loc.name,
      code,
      views: byCode.get(code) ?? 0,
      x: event.clientX - bounds.left,
      y: event.clientY - bounds.top,
    })
  }

  function zoomAt(factor: number, point?: Point) {
    const bounds = viewportRef.current?.getBoundingClientRect()
    if (!bounds) return
    setHover(null)
    setView((current) => {
      const scale = clamp(current.scale * factor, MIN_SCALE, MAX_SCALE)
      const ratio = scale / current.scale
      const offsetX = (point?.x ?? bounds.width / 2) - bounds.width / 2
      const offsetY = (point?.y ?? bounds.height / 2) - bounds.height / 2
      return clampView({
        scale,
        x: offsetX - ratio * (offsetX - current.x),
        y: offsetY - ratio * (offsetY - current.y),
      }, bounds.width, bounds.height)
    })
  }

  function handlePointerDown(event: React.PointerEvent<HTMLDivElement>) {
    if (event.pointerType === 'mouse' && event.button !== 0) return
    event.currentTarget.setPointerCapture(event.pointerId)
    pointersRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY })
    gestureRef.current = gestureFor([...pointersRef.current.values()])
    setDragging(true)
    setHover(null)
  }

  function handlePointerMove(event: React.PointerEvent<HTMLDivElement>) {
    if (!pointersRef.current.has(event.pointerId)) return
    pointersRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY })
    const previous = gestureRef.current
    const next = gestureFor([...pointersRef.current.values()])
    const bounds = viewportRef.current?.getBoundingClientRect()
    if (!previous || !bounds) return

    setView((current) => {
      const x = current.x + next.center.x - previous.center.x
      const y = current.y + next.center.y - previous.center.y
      return clampView({ scale: current.scale, x, y }, bounds.width, bounds.height)
    })
    gestureRef.current = next
  }

  function handlePointerEnd(event: React.PointerEvent<HTMLDivElement>) {
    pointersRef.current.delete(event.pointerId)
    gestureRef.current = pointersRef.current.size > 0
      ? gestureFor([...pointersRef.current.values()])
      : null
    setDragging(pointersRef.current.size > 0)
  }

  function centerOnCountry(code: string | null) {
    const viewport = viewportRef.current
    const path = code ? pathRefs.current.get(code) : null
    const svg = path?.ownerSVGElement
    if (!viewport || !path || !svg) {
      setView({ scale: DEFAULT_SCALE, x: 0, y: 0 })
      return
    }

    const viewportBounds = viewport.getBoundingClientRect()
    const box = path.getBBox()
    const svgViewBox = svg.viewBox.baseVal
    const fit = Math.min(viewportBounds.width / svgViewBox.width, viewportBounds.height / svgViewBox.height)
    const renderedWidth = svgViewBox.width * fit
    const renderedHeight = svgViewBox.height * fit
    const countryX = (viewportBounds.width - renderedWidth) / 2 + (box.x + box.width / 2 - svgViewBox.x) * fit
    const countryY = (viewportBounds.height - renderedHeight) / 2 + (box.y + box.height / 2 - svgViewBox.y) * fit
    setView(clampView({
      scale: DEFAULT_SCALE,
      x: -DEFAULT_SCALE * (countryX - viewportBounds.width / 2),
      y: -DEFAULT_SCALE * (countryY - viewportBounds.height / 2),
    }, viewportBounds.width, viewportBounds.height))
  }

  function resetView() {
    setHover(null)
    centerOnCountry(dominantCountry)
  }

  useEffect(() => {
    if (!world || !dominantCountry || centeredCountryRef.current === dominantCountry) return
    const frame = window.requestAnimationFrame(() => {
      centerOnCountry(dominantCountry)
      centeredCountryRef.current = dominantCountry
    })
    return () => window.cancelAnimationFrame(frame)
  }, [world, dominantCountry])

  return (
    <div className="dash-audience-map">
      <div
        ref={viewportRef}
        className={`dash-audience-map-viewport${dragging ? ' is-dragging' : ''}`}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerEnd}
        onPointerCancel={handlePointerEnd}
      >
        {world ? (
          <svg
            viewBox={world.viewBox}
            className="dash-audience-map-svg"
            role="img"
            aria-label={t('audienceMap.label')}
            style={{ transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale})` }}
          >
            {world.locations.map((loc) => {
              const code = loc.id.toUpperCase()
              const active = (byCode.get(code) ?? 0) > 0
              return (
                <path
                  key={loc.id}
                  ref={(node) => { if (node) pathRefs.current.set(code, node); else pathRefs.current.delete(code) }}
                  d={loc.path}
                  fill={fillFor(code)}
                  className={active ? 'is-active' : ''}
                  onMouseMove={(event) => handleMove(event, loc)}
                  onMouseLeave={() => setHover(null)}
                />
              )
            })}
          </svg>
        ) : (
          <div className="dash-audience-map-skeleton sk" aria-hidden="true" />
        )}

        {hover ? (
          <div
            className="dash-audience-map-tooltip"
            style={{ left: hover.x, top: hover.y }}
            role="status"
          >
            <span className="t-name">{hover.name}</span>
            <span className="t-views">
              {hover.views > 0 ? t('audience.views', { count: hover.views.toLocaleString() }) : t('audience.noViews')}
            </span>
          </div>
        ) : null}

        <div className="dash-audience-map-controls" onPointerDown={(event) => event.stopPropagation()}>
          <button type="button" aria-label={t('audienceMap.zoomIn')} onClick={() => zoomAt(1.3)}><IconPlus size={14} /></button>
          <button type="button" aria-label={t('audienceMap.zoomOut')} onClick={() => zoomAt(1 / 1.3)}><IconMinus size={14} /></button>
          <button type="button" aria-label={t('audienceMap.center')} onClick={resetView}><IconCurrentLocation size={14} /></button>
        </div>
        <span className="dash-audience-map-gesture-hint" aria-hidden="true">{t('audienceMap.dragHint')}</span>
      </div>

      <div className="dash-audience-map-legend" aria-hidden="true">
        <span>{t('audienceMap.less')}</span>
        <span className="dash-audience-map-legend-bar" />
        <span>{t('audienceMap.more')}</span>
      </div>
    </div>
  )
}
